/**
 * Recently Viewed Products Module
 * Handles storing and displaying recently viewed products
 */

class recordRecentlyViewedProducts extends HTMLElement {
  constructor() {
    super();
    this.name = "wokiee_recently_viewed";
  }

  connectedCallback() {
    // Only record on product pages
    if (this.isProductPage()) {
      this.recordRecentlyViewed();
    }
  }

  isProductPage() {
    return window.location.pathname.indexOf('/products/') !== -1;
  }

  getProductHandle() {
    // Try multiple selectors to find product handle
    const selectors = [
      '[data-product-handle]',
      'body[data-product-id]',
      '.product-form[data-product-handle]',
      'form[action*="/cart/add"]'
    ];
    
    for (const selector of selectors) {
      const element = document.querySelector(selector);
      if (element) {
        const handle = element.getAttribute('data-product-handle') || 
                      element.getAttribute('data-product-id') ||
                      this.extractHandleFromUrl();
        if (handle) return handle;
      }
    }
    
    // Fallback: extract from URL
    return this.extractHandleFromUrl();
  }

  extractHandleFromUrl() {
    const pathMatch = window.location.pathname.match(/\/products\/([^\/\?]+)/);
    return pathMatch ? pathMatch[1] : null;
  }

  config() {
    const count = this.getAttribute('data-count');
    return {
      howManyToStoreInMemory: count ? Number(count) + 1 : 21,
      settings: this.getAttribute('data-settings') || ''
    };
  }

  // Use localStorage instead of cookies for better reliability
  setStorage(name, value) {
    try {
      if (typeof Storage !== 'undefined') {
        localStorage.setItem(name, JSON.stringify(value));
        // Also set cookie as fallback
        this.setCookie(name, value.join(' '), 30);
      } else {
        this.setCookie(name, value.join(' '), 30);
      }
    } catch (e) {
      console.warn('Failed to save recently viewed products:', e);
      this.setCookie(name, value.join(' '), 30);
    }
  }

  getStorage(name) {
    try {
      if (typeof Storage !== 'undefined') {
        const stored = localStorage.getItem(name);
        if (stored) {
          return JSON.parse(stored);
        }
      }
    } catch (e) {
      console.warn('Failed to read from localStorage, using cookie fallback:', e);
    }
    
    // Fallback to cookie
    const cookieData = this.getCookie(name);
    if (cookieData && cookieData.length > 0 && cookieData[0] !== '') {
      return cookieData;
    }
    return [];
  }

  setCookie(name, value, days) {
    try {
      var expires = "";
      if (days) {
        var date = new Date();
        date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
        expires = "; expires=" + date.toUTCString();
      }
      document.cookie = name + "=" + (value || "") + expires + "; path=/; SameSite=Lax";
    } catch (e) {
      console.warn('Failed to set cookie:', e);
    }
  }

  getCookie(name) {
    try {
      var nameEQ = name + "=";
      var ca = document.cookie.split(';');
      for (var i = 0; i < ca.length; i++) {
        var c = ca[i];
        while (c.charAt(0) == ' ') c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) == 0) {
          var arr = c.substring(nameEQ.length, c.length);
          return arr ? arr.split(' ').filter(Boolean) : [];
        }
      }
    } catch (e) {
      console.warn('Failed to read cookie:', e);
    }
    return [];
  }

  recordRecentlyViewed() {
    if (!this.isProductPage()) return;

    const productHandle = this.getProductHandle();
    if (!productHandle) {
      console.warn('Could not find product handle to record');
      return;
    }

    try {
      const config = this.config();
      let recentlyViewed = this.getStorage(this.name);
      
      // Ensure it's an array
      if (!Array.isArray(recentlyViewed)) {
        recentlyViewed = [];
      }

      // Remove duplicates and current product
      recentlyViewed = recentlyViewed.filter(handle => handle && handle !== productHandle);
      
      // Add current product to the beginning
      recentlyViewed.unshift(productHandle);
      
      // Limit the number of stored products
      recentlyViewed = recentlyViewed.slice(0, config.howManyToStoreInMemory);
      
      // Save updated list
      this.setStorage(this.name, recentlyViewed);
    } catch (e) {
      console.error('Error recording recently viewed product:', e);
    }
  }
}

class recentlyViewedProducts extends recordRecentlyViewedProducts {
  constructor() {
    super();
  }

  connectedCallback() {
    // Wait for DOM and routes to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      // Wait a bit for routes to be defined
      setTimeout(() => this.init(), 100);
    }
  }

  init() {
    // Check if routes are available
    if (!window.routes || !window.routes.collections_url) {
      console.warn('Routes not available, retrying...');
      setTimeout(() => this.init(), 200);
      return;
    }

    let currentproducts = this.getStorage(this.name);
    
    // Ensure it's an array
    if (!Array.isArray(currentproducts)) {
      currentproducts = [];
    }

    // Remove current product from list if on product page
    if (this.isProductPage()) {
      const productHandle = this.getProductHandle();
      if (productHandle) {
        currentproducts = currentproducts.filter(handle => handle !== productHandle);
      }
    }

    // Filter out empty values
    currentproducts = currentproducts.filter(Boolean);

    if (currentproducts.length <= 0) {
      // Show placeholder in design mode
      if (Shopify && Shopify.designMode) {
        this.getProducts("");
      } else {
        // Hide section if no products
        this.classList.add("hide");
        this.showEmptyMessage();
      }
      return;
    }

    const productHandles = currentproducts.join('||');
    this.getProducts(productHandles);
  }

  showEmptyMessage() {
    const contentDiv = this.querySelector('.recently-viewed-products-content');
    if (contentDiv && !contentDiv.querySelector('.recently-viewed-empty')) {
      const emptyMsg = document.createElement('div');
      emptyMsg.className = 'recently-viewed-empty';
      emptyMsg.style.cssText = 'text-align: center; padding: 2rem; color: #666;';
      emptyMsg.textContent = 'No recently viewed products yet.';
      contentDiv.appendChild(emptyMsg);
    }
  }

  getProducts(currentproducts) {
    const _this = this;
    const config = this.config();
    const contentDiv = this.querySelector('.recently-viewed-products-content');
    
    if (!contentDiv) {
      console.error('Could not find .recently-viewed-products-content element');
      return;
    }

    // Check if routes are available
    if (!window.routes || !window.routes.collections_url) {
      console.error('Routes not available');
      this.showEmptyMessage();
      return;
    }

    const url = `${window.routes.collections_url}/all?view=ajax_recently_viewed&sort_by=${currentproducts}|split|${config.settings}`;
    
    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.text();
      })
      .then((responseText) => {
        if (!responseText || responseText.trim() === '') {
          _this.classList.add("hide");
          _this.showEmptyMessage();
          return;
        }

        _this.classList.remove("hide");
        contentDiv.innerHTML = responseText;
        
        // Reinitialize slider after content is loaded
        _this.reinitializeSlider();
        
        // Trigger lazy loading for images if needed
        _this.triggerLazyLoading();
      })
      .catch((error) => {
        console.error('Error fetching recently viewed products:', error);
        _this.classList.add("hide");
        _this.showEmptyMessage();
      });
  }

  reinitializeSlider() {
    // Find swiper-slider elements in the newly loaded content
    const swiperElements = this.querySelectorAll('swiper-slider');
    
    if (swiperElements.length > 0) {
      // Wait a bit for DOM to settle
      setTimeout(() => {
        swiperElements.forEach((swiperEl) => {
          // Check if swiper is already initialized
          if (!swiperEl.swiper) {
            // Trigger initialization by dispatching swiperLoaded event
            if (typeof Swiper === 'function') {
              window.dispatchEvent(new CustomEvent('swiperLoaded'));
            } else {
              // If Swiper isn't loaded yet, wait for it
              const checkSwiper = setInterval(() => {
                if (typeof Swiper === 'function') {
                  clearInterval(checkSwiper);
                  window.dispatchEvent(new CustomEvent('swiperLoaded'));
                }
              }, 100);
              
              // Timeout after 5 seconds
              setTimeout(() => clearInterval(checkSwiper), 5000);
            }
          }
        });
      }, 100);
    }
  }

  triggerLazyLoading() {
    // Trigger lazy loading for images
    const lazyImages = this.querySelectorAll('img[loading="lazy"], img[data-src]');
    if (lazyImages.length > 0 && 'IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            if (img.dataset.src) {
              img.src = img.dataset.src;
              img.removeAttribute('data-src');
            }
            observer.unobserve(img);
          }
        });
      });

      lazyImages.forEach(img => imageObserver.observe(img));
    }
  }
}

// Register custom elements
if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', recentlyViewedProducts);
}

// Register record element only if not already registered
if (!customElements.get('recently-viewed-products-record')) {
  customElements.define('recently-viewed-products-record', recordRecentlyViewedProducts);
}

// Auto-record on product pages even if section is not present
(function() {
  function autoRecordProduct() {
    // Only run on product pages
    if (window.location.pathname.indexOf('/products/') === -1) {
      return;
    }

    // Check if we already recorded this product in this session
    const sessionKey = 'rvp_recorded_' + window.location.pathname;
    if (sessionStorage.getItem(sessionKey)) {
      return;
    }

    // Extract product handle from URL or DOM
    let productHandle = null;
    
    // Try to get from DOM first
    const handleElement = document.querySelector('[data-product-handle]');
    if (handleElement) {
      productHandle = handleElement.getAttribute('data-product-handle');
    }
    
    // Fallback: extract from URL
    if (!productHandle) {
      const pathMatch = window.location.pathname.match(/\/products\/([^\/\?]+)/);
      if (pathMatch) {
        productHandle = pathMatch[1];
      }
    }

    if (!productHandle) {
      return;
    }

    // Record the product
    try {
      const storageName = "wokiee_recently_viewed";
      let recentlyViewed = [];
      
      // Try localStorage first
      if (typeof Storage !== 'undefined') {
        const stored = localStorage.getItem(storageName);
        if (stored) {
          recentlyViewed = JSON.parse(stored);
        }
      }
      
      // Fallback to cookie
      if (!Array.isArray(recentlyViewed) || recentlyViewed.length === 0) {
        const nameEQ = storageName + "=";
        const ca = document.cookie.split(';');
        for (let i = 0; i < ca.length; i++) {
          let c = ca[i];
          while (c.charAt(0) == ' ') c = c.substring(1, c.length);
          if (c.indexOf(nameEQ) == 0) {
            const arr = c.substring(nameEQ.length, c.length);
            recentlyViewed = arr ? arr.split(' ').filter(Boolean) : [];
            break;
          }
        }
      }

      // Ensure it's an array
      if (!Array.isArray(recentlyViewed)) {
        recentlyViewed = [];
      }

      // Remove duplicates and current product
      recentlyViewed = recentlyViewed.filter(handle => handle && handle !== productHandle);
      
      // Add current product to the beginning
      recentlyViewed.unshift(productHandle);
      
      // Limit to 20 products
      recentlyViewed = recentlyViewed.slice(0, 21);
      
      // Save to localStorage
      if (typeof Storage !== 'undefined') {
        localStorage.setItem(storageName, JSON.stringify(recentlyViewed));
      }
      
      // Also save to cookie as fallback
      const date = new Date();
      date.setTime(date.getTime() + (30 * 24 * 60 * 60 * 1000));
      document.cookie = storageName + "=" + recentlyViewed.join(' ') + "; expires=" + date.toUTCString() + "; path=/; SameSite=Lax";
      
      // Mark as recorded in this session
      sessionStorage.setItem(sessionKey, 'true');
    } catch (e) {
      console.warn('Failed to auto-record product:', e);
    }
  }

  // Run when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoRecordProduct);
  } else {
    // If DOM is already loaded, wait a bit for product data to be available
    setTimeout(autoRecordProduct, 500);
  }
})();
